import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Delete, Image, Videocam } from '@mui/icons-material';
import { HANDLES, type AssetNodeData, useCanvasStore } from '../useCanvasStore';
import { getAssetSrc } from '../../utils/assetUrl';

function AssetNode({ id, data }: NodeProps & { data: AssetNodeData }) {
  const removeNode = useCanvasStore((s) => s.removeNode);

  const isVideo = data.assetType === 'video';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 200,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          bgcolor: '#37474f',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isVideo ? <Videocam sx={{ fontSize: 14 }} /> : <Image sx={{ fontSize: 14 }} />}
          <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>
            {data.label || (isVideo ? 'Video Asset' : 'Image Asset')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Preview */}
      <Box sx={{ p: 1 }}>
        {isVideo ? (
          <video
            src={data.dataUrl}
            controls
            style={{ width: '100%', borderRadius: 4, maxHeight: 150 }}
          />
        ) : (
          <img
            src={data.dataUrl?.startsWith('http') ? data.dataUrl : getAssetSrc(data.dataUrl || '', data.mimeType || 'image/png')}
            alt={data.label || 'Asset'}
            style={{ width: '100%', borderRadius: 4, maxHeight: 150, objectFit: 'cover' }}
          />
        )}
        {data.prompt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }} noWrap>
            {data.prompt}
          </Typography>
        )}
      </Box>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id={isVideo ? HANDLES.videoOut : HANDLES.imageOut}
        style={{ background: isVideo ? '#42a5f5' : '#90caf9', width: 10, height: 10 }}
      />
    </Box>
  );
}

export default memo(AssetNode);
